import React, { useState } from 'react';
import { useI18n } from '../i18n';
import { Activity, Cpu, Filter, Lock } from 'lucide-react';
import { trackAnalyticsFilterChange } from '../lib/analytics';

interface AuditLogEntry {
  id: string;
  time: string;
  model: string;
  team: string;
  tokens: string;
  cost: string;
  dlpAction: string;
  hasRedaction: boolean;
}

export const AnalyticsShowcase: React.FC = () => {
  const { t } = useI18n();
  const [filter, setFilter] = useState<'ALL' | 'DLP_ONLY'>('ALL');

  const handleFilterChange = (newFilter: 'ALL' | 'DLP_ONLY') => {
    setFilter(newFilter);
    trackAnalyticsFilterChange(newFilter);
  };

  const auditLogs: AuditLogEntry[] = [
    {
      id: 'log_901',
      time: 'Just now',
      model: 'openai/gpt-4o',
      team: 'Risk & Compliance',
      tokens: '1,420 (820 in / 600 out)',
      cost: '$0.0142',
      dlpAction: 'Redacted (Credit Card & SSN)',
      hasRedaction: true,
    },
    {
      id: 'log_902',
      time: '2s ago',
      model: 'anthropic/claude-3-5-sonnet',
      team: 'Core Engineering',
      tokens: '3,850 (2,100 in / 1,750 out)',
      cost: '$0.0385',
      dlpAction: 'Clean (Zero PII)',
      hasRedaction: false,
    },
    {
      id: 'log_903',
      time: '5s ago',
      model: 'ollama/llama-3-70b (Local)',
      team: 'Internal AI Lab',
      tokens: '840 (420 in / 420 out)',
      cost: '$0.0000 (Air-Gapped)',
      dlpAction: 'Redacted (API Secret)',
      hasRedaction: true,
    },
    {
      id: 'log_904',
      time: '12s ago',
      model: 'cloudflare/@cf/meta/llama-3-8b',
      team: 'Mobile Team',
      tokens: '512 (256 in / 256 out)',
      cost: '$0.0025',
      dlpAction: 'Clean (Zero PII)',
      hasRedaction: false,
    },
    {
      id: 'log_905',
      time: '18s ago',
      model: 'openai/gpt-4o',
      team: 'Finance Operations',
      tokens: '2,910 (1,400 in / 1,510 out)',
      cost: '$0.0291',
      dlpAction: 'Redacted (IBAN & Tax ID)',
      hasRedaction: true,
    },
  ];

  const filteredLogs =
    filter === 'DLP_ONLY' ? auditLogs.filter((l) => l.hasRedaction) : auditLogs;

  return (
    <section id="analytics" className="py-20 md:py-28 relative bg-slate-100/70 dark:bg-navy-950/40 border-y border-slate-200 dark:border-navy-800/80 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold-500/10 border border-gold-500/30 text-xs font-semibold text-gold-600 dark:text-gold-400 mb-4 uppercase tracking-wider">
            <Activity className="w-3.5 h-3.5" />
            <span>{t.analytics.badge}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-4">
            {t.analytics.title}
          </h2>
          <p className="text-base text-slate-600 dark:text-slate-300">
            {t.analytics.subtitle}
          </p>
        </div>

        {/* Top 4 KPI Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-10">
          <div className="glow-card rounded-2xl p-5 border-slate-200 dark:border-navy-700/80">
            <span className="text-xs text-slate-500 dark:text-slate-400">{t.analytics.metrics.tokens}</span>
            <div className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mt-1 font-mono">
              12.8M
            </div>
            <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-mono mt-2 block font-semibold">
              ↑ 24% this week
            </span>
          </div>

          <div className="glow-card rounded-2xl p-5 border-slate-200 dark:border-navy-700/80">
            <span className="text-xs text-slate-500 dark:text-slate-400">{t.analytics.metrics.spend}</span>
            <div className="text-2xl sm:text-3xl font-extrabold text-gold-600 dark:text-gold-400 mt-1 font-mono">
              $184.20
            </div>
            <span className="text-[10px] text-slate-500 dark:text-slate-400 font-mono mt-2 block">
              Within $250.00 Limit
            </span>
          </div>

          <div className="glow-card rounded-2xl p-5 border-slate-200 dark:border-navy-700/80">
            <span className="text-xs text-slate-500 dark:text-slate-400">{t.analytics.metrics.dlpBlocked}</span>
            <div className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mt-1 font-mono">
              1,492
            </div>
            <span className="text-[10px] text-gold-600 dark:text-gold-400 font-mono mt-2 block font-semibold">
              100% In-Memory Intercept
            </span>
          </div>

          <div className="glow-card rounded-2xl p-5 border-slate-200 dark:border-navy-700/80">
            <span className="text-xs text-slate-500 dark:text-slate-400">{t.analytics.metrics.latency}</span>
            <div className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mt-1 font-mono">
              14ms
            </div>
            <span className="text-[10px] text-blue-600 dark:text-blue-400 font-mono mt-2 block font-semibold">
              Cloudflare Edge Fastpath
            </span>
          </div>
        </div>

        {/* Dashboard Grid: Live Audit Stream Table & Provider Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Append-Only Audit Stream Table (2 Columns) */}
          <div className="lg:col-span-2 glow-card rounded-2xl p-6 border-slate-200 dark:border-navy-700/80 flex flex-col justify-between">
            <div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 pb-3 border-b border-slate-200 dark:border-navy-800">
                <div>
                  <h3 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    {t.analytics.liveAuditTitle}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t.analytics.liveAuditSub}</p>
                </div>

                <div className="flex items-center gap-1.5 p-1 rounded-lg bg-slate-100 dark:bg-navy-950/80 border border-slate-300 dark:border-navy-800 text-xs">
                  <Filter className="w-3 h-3 text-slate-500 dark:text-slate-400 ml-1" />
                  <button
                    onClick={() => handleFilterChange('ALL')}
                    className={`px-2 py-0.5 rounded font-medium ${
                      filter === 'ALL'
                        ? 'bg-gold-500 text-navy-950 font-bold'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => handleFilterChange('DLP_ONLY')}
                    className={`px-2 py-0.5 rounded font-medium ${
                      filter === 'DLP_ONLY'
                        ? 'bg-gold-500 text-navy-950 font-bold'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    DLP Intercepts
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="text-slate-500 dark:text-slate-400 uppercase font-mono text-[10px] tracking-wider border-b border-slate-200 dark:border-navy-800 pb-2">
                      <th className="pb-2">{t.analytics.tableHeaders.time}</th>
                      <th className="pb-2">{t.analytics.tableHeaders.model}</th>
                      <th className="pb-2">{t.analytics.tableHeaders.team}</th>
                      <th className="pb-2">{t.analytics.tableHeaders.tokens}</th>
                      <th className="pb-2">{t.analytics.tableHeaders.cost}</th>
                      <th className="pb-2">{t.analytics.tableHeaders.dlpFlag}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-navy-850 font-mono">
                    {filteredLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-slate-50 dark:hover:bg-navy-900/40 transition-colors">
                        <td className="py-2.5 text-slate-500 dark:text-slate-400">{log.time}</td>
                        <td className="py-2.5 text-slate-900 dark:text-slate-200 font-semibold">
                          {log.model}
                        </td>
                        <td className="py-2.5 text-slate-600 dark:text-slate-400">{log.team}</td>
                        <td className="py-2.5 text-slate-700 dark:text-slate-300">{log.tokens}</td>
                        <td className="py-2.5 text-gold-600 dark:text-gold-400 font-bold">{log.cost}</td>
                        <td className="py-2.5">
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-sans font-semibold ${
                              log.hasRedaction
                                ? 'bg-gold-500/20 text-gold-700 dark:text-gold-400 border border-gold-500/40'
                                : 'bg-slate-100 dark:bg-navy-800 text-slate-600 dark:text-slate-400'
                            }`}
                          >
                            {log.dlpAction}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-slate-200 dark:border-navy-850 flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400 font-mono">
              <Lock className="w-3.5 h-3.5 text-gold-500" />
              <span>{t.analytics.immutableNotice}</span>
            </div>
          </div>

          {/* Provider Cost Distribution (1 Column) */}
          <div className="glow-card rounded-2xl p-6 border-slate-200 dark:border-navy-700/80 flex flex-col justify-between">
            <div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white mb-4 pb-3 border-b border-slate-200 dark:border-navy-800">
                {t.analytics.modelShareTitle}
              </h3>

              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-xs mb-1 font-mono">
                    <span className="text-slate-700 dark:text-slate-300">OpenAI (GPT-4o)</span>
                    <span className="text-gold-600 dark:text-gold-400 font-bold">$88.40 (48%)</span>
                  </div>
                  <div className="w-full bg-slate-100 dark:bg-navy-950 h-2 rounded-full overflow-hidden">
                    <div className="bg-gold-500 h-full rounded-full" style={{ width: '48%' }} />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs mb-1 font-mono">
                    <span className="text-slate-700 dark:text-slate-300">Anthropic (Claude 3.5)</span>
                    <span className="text-blue-600 dark:text-blue-400 font-bold">$58.94 (32%)</span>
                  </div>
                  <div className="w-full bg-slate-100 dark:bg-navy-950 h-2 rounded-full overflow-hidden">
                    <div className="bg-blue-500 h-full rounded-full" style={{ width: '32%' }} />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs mb-1 font-mono">
                    <span className="text-slate-700 dark:text-slate-300">Ollama (Private Llama 3)</span>
                    <span className="text-emerald-600 dark:text-emerald-400 font-bold">$27.63 (15%)</span>
                  </div>
                  <div className="w-full bg-slate-100 dark:bg-navy-950 h-2 rounded-full overflow-hidden">
                    <div className="bg-emerald-500 h-full rounded-full" style={{ width: '15%' }} />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs mb-1 font-mono">
                    <span className="text-slate-700 dark:text-slate-300">Cloudflare AI Gateway</span>
                    <span className="text-purple-600 dark:text-purple-400 font-bold">$9.23 (5%)</span>
                  </div>
                  <div className="w-full bg-slate-100 dark:bg-navy-950 h-2 rounded-full overflow-hidden">
                    <div className="bg-purple-500 h-full rounded-full" style={{ width: '5%' }} />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-slate-200 dark:border-navy-850">
              <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                <Cpu className="w-3.5 h-3.5 text-gold-500" />
                Automatic Multi-Model Gateway Fallback Active
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
